/**
 * assets/backcountry-tabs.js
 * A Custom Element that handles both 'Standard Tabs' and 'Accordion Tabs' logic.
 * If the class 'backcountry-accordion-tab-container' is present, it acts as a hybrid (accordion
 * on mobile, tabs on desktop). Otherwise, it defaults to standard exclusive tab behavior.
 */
if (!customElements.get('backcountry-tabs')) {
  class BackcountryTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        button: '[role="tab"]',
        panel: '[role="tabpanel"]'
      };
      
      // Bind methods to 'this' to ensure context is preserved in event listeners
      this.onResize = this.onResize.bind(this);
      this.onTabClick = this.onTabClick.bind(this);
    }

    get isAccordionMode() {
      // Check if this specific instance is intended to be a hybrid accordion
      return this.classList.contains('backcountry-accordion-tab-container');
    }

    get isDesktop() {
      return window.matchMedia('(min-width: 768px)').matches;
    }

    connectedCallback() {
      // Cache elements within this specific component instance
      this.buttons = this.querySelectorAll(this.selectors.button);
      this.panels = this.querySelectorAll(this.selectors.panel);

      // Add Event Listeners
      this.buttons.forEach(btn => {
        btn.addEventListener('click', this.onTabClick);
      });

      // Setup Resize Observer (only if it's the hybrid accordion type)
      if (this.isAccordionMode) {
        window.addEventListener('resize', this.onResize);
      }
    }

    disconnectedCallback() {
      // Cleanup listeners when element is removed from DOM
      if (this.isAccordionMode) {
        window.removeEventListener('resize', this.onResize);
      }
    }

    /**
     * Handles clicks for both Standard and Hybrid modes
     */
    onTabClick(e) {
      const clickedBtn = e.currentTarget;
      const targetId = clickedBtn.getAttribute('aria-controls');
      const targetPanel = this.querySelector(`#${targetId}`);
      const isAlreadyActive = clickedBtn.getAttribute('aria-selected') === 'true';

      // Mobile Accordion Behavior (Only if hybrid class is present AND we are on mobile)
      if (this.isAccordionMode && !this.isDesktop) {
        if (isAlreadyActive) {
          // Close it (Toggle behavior)
          clickedBtn.setAttribute('aria-selected', 'false');
          if (targetPanel) targetPanel.setAttribute('hidden', '');
        } else {
          // Open it
          clickedBtn.setAttribute('aria-selected', 'true');
          if (targetPanel) targetPanel.removeAttribute('hidden');
        }
      } 
      // Standard Tab Behavior (Desktop or Legacy)
      else {
        // Deactivate ALL tabs in this component
        this.buttons.forEach(btn => btn.setAttribute('aria-selected', 'false'));
        this.panels.forEach(panel => panel.setAttribute('hidden', ''));

        // Activate the clicked one
        clickedBtn.setAttribute('aria-selected', 'true');
        if (targetPanel) targetPanel.removeAttribute('hidden');
      }
    }

    /**
     * Handles logic when resizing from Mobile -> Desktop
     * Ensures we don't end up with 0 open tabs in Desktop mode
     */
    onResize() {
      // We only care if we are moving TO desktop
      if (!this.isDesktop) return;

      const activeTab = this.querySelector(`${this.selectors.button}[aria-selected="true"]`);

      // If no tab is active (which is valid in mobile accordion, but invalid in desktop tabs), open the first one.
      if (!activeTab && this.buttons.length > 0) {
        const firstTab = this.buttons[0];
        const targetId = firstTab.getAttribute('aria-controls');
        const targetPanel = this.querySelector(`#${targetId}`);

        firstTab.setAttribute('aria-selected', 'true');
        if (targetPanel) targetPanel.removeAttribute('hidden');
      }
    }
  }

  customElements.define('backcountry-tabs', BackcountryTabs);
}